import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays } from 'date-fns';
import { RootState } from '../../redux/rootReducer';

import useLocales from '../useLocales';
import {
  DateRangeFilter,
  MultiselectItem,
  SortBy,
  TableSkipLimit
} from '../../@types/filters';
import useFilters from '../useFilters';
import {
  AcsMessagesRow,
  LogMessage,
  TableHeaderCell
} from '../../@types/tableRows';
import {
  fetchAcsMessages,
  setUrlParams
} from '../../redux/slices/acs/acsMessages';
import useAuth from '../useAuth';

// -----------------------------------------------------------------------

export type AcsMessagesFiltersState = {
  tradingPartner: Array<MultiselectItem>;
  documentType: Array<MultiselectItem>;
  ediCustomers: Array<MultiselectItem>;
  timePeriod: DateRangeFilter;
  messageId: string;
  jobId: string;
  transactionStatus: Array<MultiselectItem>;
};

function useAcsMessages() {
  const dispatch = useDispatch();
  const { response, params, APIStatus, error } = useSelector(
    (state: RootState) => state.acsMessages
  );

  const {
    response: {
      acsFilters: { acsEdiCustomers, acsTradingPartner, acsEdiDocumentTypes }
    }
  } = useFilters();

  const { t } = useTranslation(['component', 'filter']);
  const { currentLang } = useLocales();
  const { user } = useAuth();

  function getMessageLogByMessageId(
    messageId: number | string
  ): Array<LogMessage> {
    const message = response.rows.find((item) => item.messageId === messageId);
    if (message) {
      return message.logMessage;
    }
    return [];
  }

  function getMessageByMessageId(
    messageId: number | string
  ): AcsMessagesRow | undefined {
    return response.rows.find((item) => item.messageId === messageId);
  }

  const columns: Array<TableHeaderCell> = useMemo(
    () => [
      {
        id: 1,
        field: 'message',
        label: t('component:message'),
        align: 'left',
        canSort: true,
        minWidth: 350
      },
      {
        id: 2,
        field: 'tradingPartnerName',
        label: t('filter:trading_partner'),
        align: 'left',
        canSort: true,
        minWidth: 180
      },
      {
        id: 3,
        field: 'jobName',
        label: t('component:job_name'),
        align: 'left',
        canSort: true
      },
      {
        id: 4,
        field: 'customerId',
        label: t('component:customer_id'),
        align: 'left',
        canSort: true
      },
      {
        id: 5,
        field: 'direction',
        label: t('component:direction'),
        align: 'left',
        canSort: true
      },
      {
        id: 6,
        field: 'documentType',
        label: t('component:document_type'),
        align: 'left',
        canSort: true
      },
      {
        id: 7,
        field: 'date',
        label: t('component:date'),
        align: 'left',
        canSort: true
      },
      {
        id: 8,
        field: 'transactionStatus',
        label: t('component:transaction_status'),
        align: 'left',
        canSort: true
      },
      {
        id: 9,
        field: 'actionButtons',
        label: t('component:action'),
        align: 'left'
      }
    ],
    [response, currentLang]
  );

  const adminOnlyFields = ['jobName', 'customerId'];

  const summary = user.isAdmin
    ? columns
    : columns.filter((col) => !adminOnlyFields.includes(col.field));

  function getFilterBarFromUrl(paramHash: string): AcsMessagesFiltersState {
    const p = decodeURIComponent(paramHash);
    const fBar: AcsMessagesFiltersState = {
      tradingPartner: [],
      documentType: [],
      ediCustomers: [],
      messageId: '',
      jobId: '',
      transactionStatus: [],
      timePeriod: {
        startFilter: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
        endFilter: format(subDays(new Date(), 30), 'yyyy-MM-dd')
      }
    };
    p.split('&').forEach((part) => {
      const [name, value] = part.split('=');
      if (name in fBar) {
        switch (name) {
          case 'messageId':
            fBar.messageId = value;
            break;
          case 'jobId':
            fBar.jobId = value;
            break;
          case 'transactionStatus': {
            const items: Array<MultiselectItem> = value
              .split('-')
              .map((id) =>
                [
                  { name: 'PENDING', id: 0 },
                  { name: 'FINISH', id: 1 },
                  { name: 'ACKNOWLEDGED', id: 2 },
                  { name: 'ERROR', id: 3 }
                ].find((c) => c.id === Number(id))
              )
              .filter((s): s is MultiselectItem => s !== undefined);
            fBar.transactionStatus = items;
            break;
          }
          case 'from':
            fBar.timePeriod = { ...fBar.timePeriod, startFilter: value };
            break;
          case 'till':
            fBar.timePeriod = { ...fBar.timePeriod, endFilter: value };
            break;
          case 'tradingPartner':
            {
              const items: Array<MultiselectItem> = value
                .split('-')
                .map((id) => acsTradingPartner.find((c) => c.id === Number(id)))
                .filter((s): s is MultiselectItem => s !== undefined);
              fBar.tradingPartner = items;
            }
            break;
          case 'acsEdiDocumentTypes':
            {
              const items: Array<MultiselectItem> = value
                .split('-')
                .map((id) =>
                  acsEdiDocumentTypes.find((c) => c.id === Number(id))
                )
                .filter((s): s is MultiselectItem => s !== undefined);
              fBar.documentType = items;
            }
            break;
          case 'ediCustomers':
            {
              const items: Array<MultiselectItem> = value
                .split('-')
                .map((id) => acsEdiCustomers.find((c) => c.id === Number(id)))
                .filter((s): s is MultiselectItem => s !== undefined);
              fBar.ediCustomers = items;
            }
            break;
        }
      } else {
        console.log(`${name} not an attr of AcsMessagesFiltersState`);
      }
    });

    return fBar;
  }

  const abortController = useRef<AbortController>(new AbortController());

  const cancelFetch = () => {
    abortController.current.abort();
    abortController.current = new AbortController();
  };

  function parseUrlParams(url: string): Record<string, any> {
    const params = new URLSearchParams(url);
    const result: Record<string, any> = {};
    params.forEach((value, key) => (result[key] = value));
    return result;
  }

  const getAcsMessages = (
    search: string,
    sortBy: SortBy,
    pagination: TableSkipLimit
  ) => {
    dispatch(setUrlParams(search));
    const paramValues = parseUrlParams(search);
    dispatch(
      fetchAcsMessages(abortController.current, paramValues, sortBy, pagination)
    );
  };

  return {
    params,
    summary,
    response,
    APIStatus,
    error,
    cancelFetch,
    getAcsMessages,
    getFilterBarFromUrl,
    getMessageLogByMessageId,
    getMessageByMessageId
  };
}

export default useAcsMessages;
